import { useEffect, useRef } from 'react';
import { Ic } from './Icon';
import { gsap, prefersReducedMotion } from './motion';
import { SectionHead, delay } from './Section';
import type { LandingContent } from './content';

/** "How it works": three steps with a connector line that draws as you scroll. */
export function HowItWorks({ c }: { c: LandingContent }) {
  const root = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = root.current;
    if (!el || prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      gsap.set('#stepsDraw', { strokeDasharray: 100, strokeDashoffset: 100 });
      gsap.to('#stepsDraw', { strokeDashoffset: 0, ease: 'none', scrollTrigger: { trigger: el, start: 'top 65%', end: 'bottom 70%', scrub: 0.6 } });
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section className="lp-section lp-section-alt" id="how" ref={root}>
      <div className="lp-container">
        <SectionHead eyebrow={c.how.eyebrow} title={c.how.title} sub={c.how.sub} />
        <div className="steps">
          <div className="steps-line" aria-hidden="true">
            <svg viewBox="0 0 100 1" preserveAspectRatio="none" focusable="false">
              <path d="M0 .5 H100" />
              <path className="draw" id="stepsDraw" d="M0 .5 H100" />
            </svg>
          </div>
          {c.how.steps.map((s, i) => (
            <div className="lp-card step" data-reveal key={s.t} style={delay(i * 140)}>
              <span className="num">{i + 1}</span>
              <h3>{s.t}</h3>
              <p>{s.d}</p>
              <span className="time">{s.time}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Industries / solutions grid. */
export function SolutionsSection({ c }: { c: LandingContent }) {
  return (
    <section className="lp-section" id="industries">
      <div className="lp-container">
        <SectionHead eyebrow={c.ind.eyebrow} title={c.ind.title} sub={c.ind.sub} />
        <div className="industries">
          {c.ind.items.map((x, i) => (
            <article className="lp-card ind" data-reveal key={x.t} style={delay((i % 2) * 100)}>
              <div className="top">
                <span className="ic">
                  <Ic name={x.icon} />
                </span>
                <span className="cat">{x.cat}</span>
              </div>
              <h3>{x.t}</h3>
              <p>{x.d}</p>
              <div className="metric">
                <b>{x.stat}</b>
                <span>{x.sl}</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Comparison table vs. traditional trackers. */
export function CompareSection({ c }: { c: LandingContent }) {
  return (
    <section className="lp-section" id="compare" style={{ paddingTop: 0 }}>
      <div className="lp-container">
        <SectionHead eyebrow={c.cmp.eyebrow} title={c.cmp.title} sub={c.cmp.sub} />
        <div className="cmp" data-reveal role="table">
          <div className="cmp-row cmp-head" role="row">
            <div role="columnheader">{c.cmp.h[0]}</div>
            <div className="us" role="columnheader">
              {c.cmp.h[1]}
            </div>
            <div role="columnheader">{c.cmp.h[2]}</div>
          </div>
          {c.cmp.rows.map((r) => (
            <div className="cmp-row" key={r[0]} role="row">
              <div role="rowheader">{r[0]}</div>
              <div className="us" role="cell">
                <Ic name="check" />
                <span>{r[1]}</span>
              </div>
              <div className="them" role="cell">
                <Ic name="minus" />
                <span>{r[2]}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
